import { useState } from 'react';
import { coverFeature, coverSideStories, siteMeta } from '@/mocks/home';

export default function CoverHero() {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  const likeCount = coverFeature.likes + (liked ? 1 : 0);

  return (
    <section id="cover" className="w-full px-5 pb-16 pt-10 md:px-8 md:pb-24 md:pt-16">
      <div className="mx-auto w-full max-w-[1320px]">
        <div className="flex flex-col gap-4 border-b border-background-300/70 pb-6 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs tracking-editorial text-foreground-500">
            <span className="text-primary-600">{siteMeta.issue}</span>
            <span>{siteMeta.dateText}</span>
            <span>{siteMeta.weekday}</span>
          </div>
          <p className="max-w-xl font-body text-sm leading-relaxed text-foreground-600">
            {siteMeta.statement}
          </p>
        </div>

        <div className="mt-9 md:mt-14">
          <p className="font-label text-xs tracking-editorial text-accent-600">
            本期特刊 · 封面故事
          </p>
          <h2 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-[1.15] text-foreground-950 md:text-5xl">
            {siteMeta.slogan}
          </h2>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:mt-12 lg:grid-cols-12 lg:gap-8">
          <article className="group relative overflow-hidden rounded-lg border border-background-200/80 lg:col-span-7">
            <div className="relative h-[400px] w-full md:h-[520px] lg:h-[620px]">
              <img
                src={coverFeature.image}
                alt="黑料专区本期封面故事配图"
                title="黑料专区 今日猛料 封面故事"
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/5" />
            </div>

            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 font-label text-[11px] tracking-editorial text-background-50">
                {coverFeature.category}
              </span>
              <h3 className="mt-4 max-w-xl font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
                {coverFeature.title}
              </h3>
              <p className="mt-3 hidden max-w-xl font-body text-sm leading-relaxed text-background-200 sm:block">
                {coverFeature.excerpt}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-background-50/25 pt-4">
                <span className="font-label text-xs text-background-200">
                  {coverFeature.author}
                </span>
                <span className="font-label text-xs text-background-200">
                  阅读约 {coverFeature.readTime}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setLiked((v) => !v)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-label text-xs transition-colors ${
                      liked
                        ? 'border-primary-400 bg-primary-500/90 text-background-50'
                        : 'border-background-50/40 text-background-100 hover:border-primary-400'
                    }`}
                  >
                    <i className={liked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                    {likeCount}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSaved((v) => !v)}
                    aria-label="收藏本期封面故事"
                    className={`flex h-8 w-8 items-center justify-center rounded-full border font-label text-xs transition-colors ${
                      saved
                        ? 'border-accent-400 bg-accent-500 text-background-50'
                        : 'border-background-50/40 text-background-100 hover:border-accent-400'
                    }`}
                  >
                    <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                  </button>
                </div>
              </div>
            </div>
          </article>

          <div className="flex flex-col gap-6 lg:col-span-5 lg:gap-8">
            <div className="flex flex-col divide-y divide-background-200/80 rounded-lg border border-background-200/80 bg-background-100/50">
              {coverSideStories.map((story) => (
                <a
                  key={story.id}
                  href="#today"
                  className="group/side flex items-start gap-4 p-4 transition-colors hover:bg-background-100 md:p-5"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md md:h-24 md:w-24">
                    <img
                      src={story.image}
                      alt={story.title}
                      title="猛料速递 最新爆料"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover/side:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="font-label text-[11px] tracking-editorial text-accent-600">
                      {story.tag}
                    </span>
                    <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover/side:text-primary-600">
                      {story.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-4 font-label text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line" />
                        {story.comments}
                      </span>
                      <span>{story.time}</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <div className="rounded-lg border border-background-200/80 bg-secondary-50 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <p className="font-label text-xs tracking-editorial text-secondary-700">
                  本期讨论热度
                </p>
                <span className="flex items-center gap-1 font-label text-xs text-primary-600">
                  <i className="ri-fire-line" />
                  实时
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div>
                  <p className="font-heading text-2xl font-bold text-foreground-950">3.2万</p>
                  <p className="mt-1 font-label text-[11px] text-foreground-500">今日讨论</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-foreground-950">18</p>
                  <p className="mt-1 font-label text-[11px] text-foreground-500">新增爆料</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">7</p>
                  <p className="mt-1 font-label text-[11px] text-foreground-500">进行中</p>
                </div>
              </div>
              <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                <div className="h-full w-[68%] rounded-full bg-accent-500" />
              </div>
              <p className="mt-3 font-body text-xs leading-relaxed text-foreground-600">
                今日猛料整体热度已超过昨日同期，评论区仍在持续更新。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}